/* eslint-disable @typescript-eslint/indent */
import React, { useReducer, useEffect, useState } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { ReduxState } from 'kb-shared';
import Honeybadger from '@honeybadger-io/js';
import { FEATURE_DEPOSIT_PROMO } from '../../../config/features';
import { Query, Mutation, useQuery } from 'react-apollo';
import { injectStripe, ReactStripeElements } from 'react-stripe-elements';
import { showErrorToast } from '../../../utilities/notificationUtils';

import { typography } from '../../../styled';
import CyclesRow from './CyclesRow';
import Payment from '../../../components/Payment/Payment';
import PackageAndTerms from './PackageAndTerms';
import SegmentedControl from '../../../components/SegmentedControl';

import { CycleType } from '../types';

import {
  graphql,
  Package,
  PatientPackage,
  PackagesResult,
  AppointmentPackage,
  PurchasePackageResponseData,
  PurchasePackageVariables,
  ValidPromo
} from 'kb-shared';
import { gql } from 'apollo-boost';

const {
  query: { PACKAGES },
  mutate: { PURCHASE_PACKAGE }
} = graphql;

const {
  sans: {
    copy: { Nano }
  }
} = typography;

interface PatientMembershipResponse {
  patient: {
    id: string;
    patientMembership: {
      id: string;
      membershipRules: Array<{
        id: string;
        code: string;
        discountId: string;
      }>;
    };
  };
}

const patientMemberships = gql`
  query patientMemberships {
    patient {
      id
      patientMembership {
        id
        membershipRules {
          code
          id
          discountId
        }
      }
    }
  }
`;

const SegmentedContainer = styled.div`
  margin-top: 20px;
`;

const Copy = styled(Nano)`
  text-align: center;
  margin: 10px 0;
`;

type State = {
  cycle?: Package;
  selectedPaymentOption?: unknown;
  promoCode?: string;
  hasAgreedToTerms: boolean;
  loading: boolean;
  selectedCycleType: CycleType;
  availableCycles: Array<Package>;
  displayedCycles: Array<Package>;
};

type Action = {
  type: string;
  payload: {
    cycle?: Package;
    selectedPaymentOption?: unknown;
    promoCode?: string;
    hasAgreedToTerms?: boolean;
    loading?: boolean;
    selectedCycleType?: CycleType;
    availableCycles?: Array<Package>;
    displayedCycles?: Array<Package>;
  };
};

const initialState: State = {
  cycle: undefined,
  selectedPaymentOption: undefined,
  promoCode: undefined,
  hasAgreedToTerms: false,
  loading: false,
  selectedCycleType: 'Egg Freezing',
  availableCycles: [],
  displayedCycles: []
};

const isDisplayedCycle = (cycle: Package, selectedCycleType: CycleType) =>
  cycle.name.includes(selectedCycleType);

const sortCycles = (a: Package, b: Package) => {
  const { name: aName } = a;
  const { name: bName } = b;
  return aName.localeCompare(bName);
};

const reducer = (state: State, action: Action) => {
  const {
    type,
    payload: {
      cycle,
      selectedPaymentOption,
      promoCode,
      hasAgreedToTerms,
      selectedCycleType,
      availableCycles,
      loading
    }
  } = action;
  const {
    availableCycles: storedAvailableCycles,
    selectedCycleType: storedSelectedCycleType
  } = state;

  switch (type) {
    case 'setCycle':
      return {
        ...state,
        cycle
      };
    case 'setSelectedPaymentOption':
      return {
        ...state,
        selectedPaymentOption
      };
    case 'setPromoCode':
      return {
        ...state,
        promoCode
      };
    case 'setHasAgreedToTerms':
      return {
        ...state,
        hasAgreedToTerms
      };
    case 'setLoading':
      return {
        ...state,
        loading
      };
    case 'setSelectedCycleType':
      return {
        ...state,
        cycle: undefined,
        selectedCycleType,
        displayedCycles: selectedCycleType
          ? storedAvailableCycles
              .filter(cycle => isDisplayedCycle(cycle, selectedCycleType))
              .sort(sortCycles)
          : []
      };
    case 'setAvailableCycles':
      return {
        ...state,
        availableCycles,
        displayedCycles: availableCycles
          ? availableCycles
              .filter(cycle => isDisplayedCycle(cycle, storedSelectedCycleType))
              .sort(sortCycles)
          : []
      };
    default:
      return state;
  }
};

type Props = {
  stripe?: ReactStripeElements.StripeProps;
  onSuccess: (cycle: PatientPackage) => void;
  onBook: () => void;
  depositPromoCode?: string;
};

const BookCycle = (props: Props) => {
  const { stripe, onSuccess, onBook, depositPromoCode } = props;
  // @ts-ignore
  const [state, dispatch] = useReducer(reducer, initialState);
  const [didSetPromos, setDidSetPromos] = useState(false);
  const {
    cycle,
    hasAgreedToTerms,
    loading,
    displayedCycles,
    availableCycles,
    selectedCycleType,
    promoCode
  } = state;
  const secondStepShouldDisplay = cycle !== undefined;
  const skip = availableCycles.length > 0;

  const { data: patientMemberData } = useQuery<PatientMembershipResponse>(patientMemberships);

  useEffect(() => {
    if (
      didSetPromos ||
      !availableCycles ||
      availableCycles.length === 0 ||
      (!patientMemberData && !promoCode)
    ) {
      return;
    }
    let promoType = 'applied';
    const membership = patientMemberData?.patient?.patientMembership;

    if (membership) {
      promoType = 'membership';
    }

    const getPromos = async () => {
      const promos: Array<ValidPromo> = [];
      if (!membership) return;
      for (const cycle of availableCycles) {
        for (const rule of membership.membershipRules) {
          try {
            const validPromo = await graphql.requests.checkPromoCode(rule.code, {
              ...cycle,
              type: 'package'
            });
            promos.push(validPromo);
            break;
          } catch (error) {
            Honeybadger.notify(error);
          }
        }
      }
      const nextCycles = availableCycles.map((cycle: Package, index: number) => {
        const promo = promos[index];
        return {
          ...cycle,
          promo,
          promoType: promo ? promoType : undefined
        };
      });
      dispatch({ type: 'setAvailableCycles', payload: { availableCycles: nextCycles } });
      setDidSetPromos(true);
    };

    getPromos();
  }, [availableCycles, didSetPromos, patientMemberData, promoCode]);

  return (
    <Query
      query={PACKAGES}
      skip={skip}
      onCompleted={({ packages }: { packages: AppointmentPackage[] }) => {
        if (skip || !packages || packages.length === 0) return;
        dispatch({ type: 'setAvailableCycles', payload: { availableCycles: packages } });
        // Do this after so we get a render
      }}
    >
      {({ loading: queryLoading }: PackagesResult) => {
        if (queryLoading || !skip) return null;

        return (
          <>
            <Copy>
              Select your cycle package below. You can use your cycles at a later date if you
              don&apos;t wish to commit to them today.
            </Copy>
            <SegmentedContainer>
              <SegmentedControl
                options={[
                  { title: 'Egg Freezing' },
                  { title: 'IVF' },
                  { title: 'IUI' },
                  { title: 'Embryo Banking' }
                ]}
                extractTitle={item => item.title.toUpperCase()}
                selectedTitle={selectedCycleType.toUpperCase()}
                onSelect={item => {
                  dispatch({
                    type: 'setSelectedCycleType',
                    payload: { selectedCycleType: item.title }
                  });
                }}
              />
            </SegmentedContainer>
            <CyclesRow
              onSelect={cycle => {
                dispatch({ type: 'setCycle', payload: { cycle } });
                if (cycle.promo) {
                  dispatch({ type: 'setPromoCode', payload: { promoCode: cycle.promo.code } });
                }
              }}
              cycles={displayedCycles}
              selected={cycle}
              selectedCycleType={selectedCycleType}
            />
            <Payment
              shouldDisplay={secondStepShouldDisplay}
              onPromoApply={promo => {
                dispatch({ type: 'setPromoCode', payload: { promoCode: promo.code } });
                const nextCycle = {
                  ...cycle,
                  promo,
                  promoType: 'applied'
                };
                dispatch({
                  type: 'setCycle',
                  payload: {
                    cycle: nextCycle
                  }
                });
                const nextCycles = availableCycles.map((c: Package) => {
                  if (c.id !== nextCycle.id) return c;
                  return nextCycle;
                });
                dispatch({ type: 'setAvailableCycles', payload: { availableCycles: nextCycles } });
              }}
              product={cycle}
            />
            <Mutation<PurchasePackageResponseData, PurchasePackageVariables>
              mutation={PURCHASE_PACKAGE}
            >
              {createPackage => {
                let rate = cycle ? cycle.rateCents : 0;
                if (cycle && cycle.promo) {
                  rate = cycle.promo.totalCents;
                }
                return (
                  <PackageAndTerms
                    shouldDisplay={secondStepShouldDisplay}
                    cycle={cycle}
                    rateCents={rate}
                    hasAgreedToTerms={hasAgreedToTerms}
                    onAgreedToTerms={hasAgreedToTerms =>
                      dispatch({ type: 'setHasAgreedToTerms', payload: { hasAgreedToTerms } })
                    }
                    isDeposit={promoCode && promoCode.toUpperCase() === depositPromoCode}
                    loading={loading}
                    onConfirmation={async () => {
                      dispatch({ type: 'setLoading', payload: { loading: true } });
                      try {
                        // Need to make sure we're handling this error
                        if (!stripe || !cycle)
                          throw new Error("We're sorry, something went wrong.");

                        const token = await stripe.createToken();
                        const tokenId = token.token && token.token.id;

                        createPackage({
                          variables: {
                            packageId: parseInt(cycle.id),
                            stripeTokenId: tokenId,
                            discountCode: promoCode
                          }
                        })
                          // @ts-ignore
                          .then(({ data: { createPatientPackage } }) => {
                            onBook();
                            onSuccess(createPatientPackage);
                          })
                          .catch(resp => {
                            Honeybadger.notify(resp);
                            showErrorToast(
                              'Something went wrong. Please make sure your card information is correct and try again.'
                            );
                            dispatch({ type: 'setLoading', payload: { loading: false } });
                          });
                      } catch (error) {
                        showErrorToast(
                          typeof error === 'string'
                            ? error
                            : "We're sorry, something went wrong. Please try again."
                        );
                        Honeybadger.notify(error);
                        dispatch({ type: 'setLoading', payload: { loading: false } });
                      }
                    }}
                  />
                );
              }}
            </Mutation>
          </>
        );
      }}
    </Query>
  );
};

const mapStateToProps = (state: ReduxState) => {
  return {
    depositPromoCode: state.features.features[FEATURE_DEPOSIT_PROMO]
  };
};
export default connect(mapStateToProps)(injectStripe(BookCycle));
